import type { MissingHandler } from 'vue-i18n';

import { humanizeColumnKey } from '@/common/helpers';
import { LANGUAGES } from './language.const';
import { Language } from './language.enum';

export function getLanguage(): Language {
  try {
    const browserLanguage = navigator.language.split('-')[0] as Language;
    const savedLanguage = localStorage.getItem('NATUREOS_LANGUAGE') as Language;

    const locale: Language = LANGUAGES.includes(browserLanguage)
      ? browserLanguage
      : Language.EN_US;

    return savedLanguage && LANGUAGES.includes(savedLanguage) ? savedLanguage : locale;
  } catch {
    return Language.EN_US;
  }
}

export const missingTranslationHandler: MissingHandler = (_, key) => {
  const match = key.match(/^(layer|statistic_values)\.(.+)$/);

  if (match) {
    // Humanize, don't just ucfirst: server-driven column/layer keys are
    // snake_case ("class_peat", "match_id") and would otherwise surface with
    // their underscores intact in dropdowns, chart titles, and legends.
    //
    // Then strip vue-i18n's message syntax. Unlike a missing key — which is
    // returned verbatim and never compiled — a `missing` handler's return value
    // IS compiled when it differs from the key, and these keys carry names the
    // server chose. Verified against vue-i18n 11.1.6: `@` throws (code 10/14)
    // and unpaired `{`/`}` throw (6/7/9), so a layer named "ndvi@2024" would
    // crash every screen listing it. `|` does not throw but is read as the
    // plural separator, silently truncating the label ("a|b" renders as "A"),
    // so it goes too. `$` is left alone: it neither throws nor is consumed, and
    // stripping it would corrupt legitimate names like "cost_$usd".
    // Strip before humanising, not after: humanising first would apply the
    // leading capital to a character that is then deleted, so "{ndvi}" would
    // surface as "ndvi" rather than "Ndvi".
    const cleaned = humanizeColumnKey(match[2].replace(/[@{}|]/g, ' '))
      .replace(/\s+/g, ' ')
      .trim();

    // A key of nothing but syntax characters sanitises to an empty string; the
    // raw key is the better label, and is safe because a value equal to the key
    // is returned uncompiled.
    return cleaned || key;
  }

  return key;
};
